import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Play, Download, Quote, Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import { motionOk } from "../lib/motionOk.js";
import { useLang } from "../i18n.jsx";
import { Magnetic } from "../lib/Interactive.jsx";
import { api } from "../api/client.js";

const BOOKING_LABELS = {
  id: { name: "Nama Anda", email: "Email", send: "Konfirmasi booking", sending: "Mengirim\u2026", back: "Kembali", error: "Gagal mengirim \u2014 coba WhatsApp.", empty: "Belum ada slot tersedia." },
  en: { name: "Your name", email: "Email", send: "Confirm booking", sending: "Sending\u2026", back: "Back", error: "Could not send \u2014 try WhatsApp.", empty: "No slots available yet." },
};

function BookingWidget() {
  const { content, lang } = useLang();
  const { bookingTimes, confirmBooking } = content;
  const L = BOOKING_LABELS[lang] || BOOKING_LABELS.id;
  const [slots, setSlots] = useState(null);
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    let alive = true;
    api
      .getBookingSlots()
      .then((rows) => { if (alive && Array.isArray(rows) && rows.length) setSlots(rows); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const options = slots
    ? slots.map((s) => ({ id: s.id, label: s.label, available: s.isAvailable !== false }))
    : (bookingTimes || []).map((label) => ({ id: null, label, available: true }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await api.createBooking({ slotId: picked.id, label: picked.label, name: form.name, email: form.email, lang });
      setStatus("done");
    } catch (err) {
      setStatus("error");
    }
  };

  const field =
    "rounded-2xl border border-paper/20 bg-transparent px-4 py-2 text-[13px] text-paper placeholder:text-paper/35 outline-none transition focus:border-acid";

  if (status === "done") {
    return (
      <p className="inline-flex items-center gap-2 rounded-full border border-acid/50 bg-acid/10 px-4 py-2 text-[13px] font-semibold text-acid">
        <Check className="w-4 h-4" /> {confirmBooking}
      </p>
    );
  }

  if (picked) {
    return (
      <form onSubmit={submit} className="flex flex-col gap-2">
        <p className="text-[13px] font-semibold text-paper/80">{picked.label}</p>
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={L.name} className={field} />
        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={L.email} className={field} />
        <div className="flex gap-2">
          <button type="submit" disabled={status === "sending"} className="rounded-full bg-acid px-4 py-2 text-[13px] font-bold text-ink transition hover:bg-paper disabled:opacity-60" data-cursor>
            {status === "sending" ? L.sending : L.send}
          </button>
          <button type="button" onClick={() => { setPicked(null); setStatus("idle"); }} className="rounded-full border border-paper/20 px-4 py-2 text-[13px] text-paper/70">
            {L.back}
          </button>
        </div>
        {status === "error" && <p className="text-[12px] text-red-400">{L.error}</p>}
      </form>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.label}
          disabled={!o.available}
          onClick={() => setPicked(o)}
          className={
            "rounded-full border px-3 py-1.5 text-[13px] transition " +
            (o.available ? "border-paper/25 text-paper/80 hover:border-acid/50" : "border-paper/10 text-paper/30 cursor-not-allowed")
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ShowreelWidget() {
  const [open, setOpen] = useState(false);
  const { content } = useLang();
  const { widgets } = content;
  const w = widgets.find((x) => x.id === "showreel");
  return (
    <>
      <Magnetic>
        <button
          onClick={() => setOpen(true)}
          className="group flex items-center gap-3 rounded-2xl border border-paper/15 bg-ink px-4 py-3 text-left transition hover:border-acid/50"
          data-cursor
        >
          <span className="grid place-items-center w-11 h-11 rounded-full bg-acid/15 text-acid"><Play className="w-5 h-5 ml-0.5" /></span>
          <span className="text-sm text-paper/80 group-hover:text-paper">{w.action}</span>
        </button>
      </Magnetic>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={motionOk ? { opacity: 0 } : false}
            animate={motionOk ? { opacity: 1 } : undefined}
            exit={motionOk ? { opacity: 0 } : undefined}
            className="fixed inset-0 z-[60] grid place-items-center bg-black/80 p-6 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={motionOk ? { scale: 0.92, opacity: 0 } : false}
              animate={motionOk ? { scale: 1, opacity: 1 } : undefined}
              exit={motionOk ? { scale: 0.94, opacity: 0 } : undefined}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="absolute -top-11 right-0 grid place-items-center w-9 h-9 rounded-full border border-paper/20 text-paper"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="relative aspect-video w-full rounded-2xl overflow-hidden border border-paper/15 bg-ink">
                <img src="/assets/showreel.jpg" alt="" className="h-full w-full object-cover opacity-60" />
                <div className="absolute inset-0 grid place-items-center">
                  <span className="grid place-items-center w-16 h-16 rounded-full bg-acid text-ink shadow-2xl"><Play className="w-7 h-7 ml-1" /></span>
                </div>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                  <p className="font-display font-bold text-white">{w.title}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function MediaKitWidget() {
  const [state, setState] = useState("idle");
  const { content } = useLang();
  const { downloading, downloaded, widgets } = content;
  const idle = (widgets.find((x) => x.id === "medkit") || {}).action;
  const label = state === "done" ? downloaded : state === "busy" ? downloading : idle;
  return (
    <button
      onClick={() => {
        if (state === "done") return;
        setState("busy");
        // Set VITE_MEDIA_KIT_URL to serve the real file; otherwise the download
        // is simulated so the demo still works without an asset.
        const url = import.meta.env.VITE_MEDIA_KIT_URL;
        if (url) {
          window.open(url, "_blank", "noopener");
          setState("done");
          return;
        }
        setTimeout(() => setState("done"), 900);
      }}
      className="flex items-center justify-center gap-2 rounded-full border border-paper/25 px-5 py-3 text-sm font-semibold text-paper transition hover:border-acid hover:bg-acid hover:text-ink"
      data-cursor
    >
      {state === "done" ? <Check className="w-4 h-4" /> : <Download className="w-4 h-4" />}
      {label || (state === "idle" ? "Download Kit" : "")}
    </button>
  );
}

function TestimonialWidget() {
  const [idx, setIdx] = useState(0);
  const { content } = useLang();
  const { testimonials } = content;
  const t = testimonials[idx % testimonials.length];
  const prev = () => setIdx((i) => (i + testimonials.length - 1) % testimonials.length);
  const next = () => setIdx((i) => (i + 1) % testimonials.length);
  return (
    <div>
      <div className="rounded-2xl border border-paper/10 bg-ink2/70 p-5">
        <Quote className="w-6 h-6 text-acid" />
        <p className="mt-3 text-sm text-paper/85 leading-relaxed">"{t.quote}"</p>
        <p className="mt-3 text-[13px] font-bold text-paper">{t.name}</p>
        <p className="text-xs uppercase tracking-wider text-paper/60">{t.role}</p>
      </div>
      <div className="mt-3 flex items-center justify-center gap-2">
        <button onClick={prev} aria-label="Prev" className="grid place-items-center w-9 h-9 rounded-full border border-paper/15 text-paper/70"><ChevronLeft className="w-4 h-4" /></button>
        <div className="flex gap-1.5">
          {testimonials.map((_, i) => (
            <span key={i} className={"h-1.5 rounded-full transition-all " + (i === idx % testimonials.length ? "w-5 bg-acid" : "w-1.5 bg-paper/25")} />
          ))}
        </div>
        <button onClick={next} aria-label="Next" className="grid place-items-center w-9 h-9 rounded-full border border-paper/15 text-paper/70"><ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

const widgetsMap = { calendly: BookingWidget, showreel: ShowreelWidget, medkit: MediaKitWidget, testimonial: TestimonialWidget };
const iconMap = { calendly: Calendar, showreel: Play, medkit: Download, testimonial: Quote };

export default function ConversionWidgets() {
  const { content } = useLang();
  const { widgets, widgetsHead } = content;
  return (
    <section id="widgets" className="relative px-6 py-20 overflow-hidden">
      <div className="pointer-events-none absolute -right-24 top-1/4 w-[440px] h-[440px] rounded-full bg-acid/[0.04] blur-3xl" />
      <div className="relative mx-auto max-w-7xl">
        <motion.div
          initial={motionOk ? { opacity: 0, y: 24 } : false}
          whileInView={motionOk ? { opacity: 1, y: 0 } : undefined}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl"
        >
          <p className="mb-4 text-[12px] uppercase tracking-[0.3em] text-acid">{widgetsHead.eyebrow}</p>
          <h2 className="font-display font-extrabold tracking-tight text-[clamp(1.8rem,4.2vw,3.2rem)] leading-[1.08]">
            {widgetsHead.titleA}
            <span className="block">{widgetsHead.titleB}</span>
          </h2>
        </motion.div>

        <div className="mt-12 grid gap-5 md:grid-cols-2">
          {widgets.map((w, i) => {
            const Body = widgetsMap[w.id] || BookingWidget;
            const Icon = iconMap[w.id] || Calendar;
            return (
              <motion.div
                key={w.id}
                initial={motionOk ? { opacity: 0, y: 30 } : false}
                whileInView={motionOk ? { opacity: 1, y: 0 } : undefined}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: (i % 2) * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-3xl border border-paper/10 bg-ink2/50 p-7"
              >
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-11 h-11 rounded-2xl bg-acid/10 text-acid"><Icon className="w-5 h-5" /></span>
                  <div>
                    <h3 className="font-display text-lg font-bold tracking-tight text-paper">{w.title}</h3>
                    <p className="text-sm text-paper/60">{w.desc}</p>
                  </div>
                </div>
                <div className="mt-5"><Body /></div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
